import { observable, computed, action, makeObservable } from 'mobx'
import { isDefaultMenuHandle, retrieveFromWindow, sortByObjectKey } from '../../../utils'
import { ICollectionLink } from '../collections.interfaces'
import { IProduct } from '../../product'
import { collectionsStore } from '../collections.store'
import * as React from 'react'
import { collectionsProductsOrder } from '../collections.data'
import { SortingOptions } from '../../../enums/sorting-options.enums'
import { filterBarStore } from '../filter-bar'

class CollectionListStore {
  @observable displayedProducts: IProduct[] = this.products
  @observable originalProducts: IProduct[] = this.products

  constructor() {
    makeObservable(this)
  }

  @computed
  get collectionRefs(): Map<string, React.RefObject<HTMLDivElement>> {
    const refMap = new Map<string, React.RefObject<HTMLDivElement>>()
    collectionsStore.allLinks.forEach((link: ICollectionLink) => {
      if (!isDefaultMenuHandle(link)) return
      refMap.set(link.handle, React.createRef<HTMLDivElement>())
    })
    return refMap
  }

  @computed
  get products(): IProduct[] {
    const productData: IProduct[] = retrieveFromWindow('productData', [])
    const products: IProduct[] = []

    // Object from window needs to be transformed to a proper array (without string indexes), which it is in liquid
    // to easily avoid adding duplicates.
    Object.keys(productData).forEach((id: string) => {
      products.push(productData[parseInt(id, 10)])
    })

    return products
  }

  displayCollections(collection: string): IProduct[] {
    const products = this.displayedProducts.filter((product: IProduct) => product.collections.indexOf(collection) > -1)

    const { currentWeeklyMenu } = collectionsStore
    if (!currentWeeklyMenu) {
      return products
    }
    if (filterBarStore.activeSortBy !== SortingOptions.DEFAULT) {
      const isDesc = filterBarStore.activeSortBy === SortingOptions.ALPHA_DESC
      return sortByObjectKey(products, 'name', isDesc)
    }
    return products
      .map((p) => ({
        ...p,
        order: collectionsProductsOrder[`${currentWeeklyMenu.week}-${currentWeeklyMenu.year}_${p.id}`] || null
      }))
      .sort((a, b) => {
        if (a.order === null) return 1
        if (b.order === null) return -1
        return a.order - b.order
      })
  }

  collections(collection: string): IProduct[] {
    const products = this.products.filter((product: IProduct) => product.collections.indexOf(collection) > -1)
    const { currentWeeklyMenu } = collectionsStore
    if (!currentWeeklyMenu) {
      return products
    }
    if (filterBarStore.activeSortBy !== SortingOptions.DEFAULT) {
      const isDesc = filterBarStore.activeSortBy === SortingOptions.ALPHA_DESC
      return sortByObjectKey(products, 'name', isDesc)
    }
    return products
      .map((p) => ({
        ...p,
        order: collectionsProductsOrder[`${currentWeeklyMenu.week}-${currentWeeklyMenu.year}_${p.id}`] || null
      }))
      .sort((a, b) => {
        if (a.order === null) return 1
        if (b.order === null) return -1
        return a.order - b.order
      })
  }

  @action
  setDisplayedProducts = (displayedProducts: IProduct[]) => {
    this.displayedProducts = displayedProducts
  }

  @action
  resetDisplayedProducts = () => {
    this.displayedProducts = this.products
  }

  getCurrentProducts(): IProduct[] {
    const { currentWeeklyMenu } = collectionsStore
    if (!currentWeeklyMenu) return []
    return collectionListStore.collections(currentWeeklyMenu.handle)
  }
}

export const collectionListStore = new CollectionListStore()
